import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Popover,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import React, { useState } from "react";
import Chart from "react-apexcharts";
import InformationCircleIcon from "../../../icons/InformationCircle";
import { BoxedLoader } from "../../BoxedLoader";
import transport from "../../../utils/transport";
import departureCity from "../../../utils/departureCity";
import period from "../../../utils/period";
import boardType from "../../../utils/boardType";
import ChevronDownIcon from "../../../icons/ChevronDown";
import { format, subDays } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { DateRangePicker } from "react-date-range";
import { useTranslation } from "react-i18next";
import { pl } from "date-fns/locale";

const PredictedTrendsForOffer = ({ categories, data, title, tooltipInfo }) => {
  const { t, i18n } = useTranslation();
  const date = new Date();

  const theme = useTheme();
  const [selectionRange, setState] = useState([
    {
      startDate: subDays(new Date("2023-06-01"), 7),
      endDate: new Date("2023-06-01"),
      key: "selection",
    },
  ]);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const chart: any = data
    ? {
        series: [
          {
            name: "Min",
            type: "column",
            data: data.min,
            color: "#f4b73f",
          },
          {
            name: "Max",
            type: "column",
            data: data.max,
            color: "#1e4354",
          },
          {
            name: "Avg",
            type: "line",
            data: data.avg,
            color: "#5fc1f2",
          },
        ],
        options: {
          chart: {
            background: "transparent",
            stacked: false,
            toolbar: {
              show: false,
            },
          },
          dataLabels: {
            enabled: false,
          },
          colors: ["#f4b73f", "#1e4354", "#5fc1f2"],
          grid: {
            borderColor: theme.palette.divider,
            strokeDashArray: 2,
          },
          legend: {
            position: "top",
            horizontalAlign: "right",
            labels: {
              colors: theme.palette.text.secondary,
            },
            show: true,
          },
          plotOptions: {
            bar: {
              columnWidth: "40%",
            },
          },
          stroke: {
            colors: ["transparent"],
            show: true,
            width: 2,
          },
          theme: {
            mode: theme.palette.mode,
          },
          tooltip: {
            theme: theme.palette.mode,
          },
          xaxis: {
            axisBorder: {
              show: true,
              color: theme.palette.divider,
            },
            axisTicks: {
              show: true,
              color: theme.palette.divider,
            },
            categories: categories,
            labels: {
              style: {
                colors: theme.palette.text.secondary,
              },
            },
          },
          yaxis: {
            axisBorder: {
              color: theme.palette.divider,
              show: true,
            },
            axisTicks: {
              color: theme.palette.divider,
              show: true,
            },
            labels: {
              style: {
                colors: theme.palette.text.secondary,
              },
            },
          },
        },
      }
    : {};
  return (
    <Card>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <CardHeader title={title} />
        <Tooltip title={tooltipInfo}>
          <InformationCircleIcon fontSize="small" />
        </Tooltip>
      </Box>
      <Divider />
      <CardContent>
        <Box sx={{ display: "flex", pt: 3 }}>
          <Button
            variant="contained"
            onClick={handleClick}
            endIcon={<ChevronDownIcon fontSize="small" />}
          >
            {format(selectionRange[0].startDate, "dd-MM-yyyy")} -{" "}
            {format(selectionRange[0].endDate, "dd-MM-yyyy")}
          </Button>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <DateRangePicker
              locale={i18n.language != "pl" ? undefined : pl}
              staticRanges={[
                {
                  label: t("other.today"),
                  hasCustomRendering: false,
                  range: () => ({
                    startDate: date,
                    endDate: date,
                  }),
                  isSelected() {
                    return false;
                  },
                },
                {
                  label: t("other.last_week"),
                  hasCustomRendering: false,
                  range: () => ({
                    startDate: subDays(date, ((date.getDay() + 7) % 7) + 6),
                    endDate: subDays(date, (date.getDay() + 7) % 7),
                  }),
                  isSelected() {
                    return false;
                  },
                },
                {
                  label: t("other.this_month"),
                  hasCustomRendering: false,
                  range: () => ({
                    startDate: new Date(date.getFullYear(), date.getMonth(), 1),
                    endDate: new Date(
                      date.getFullYear(),
                      date.getMonth() + 1,
                      0
                    ),
                  }),
                  isSelected: () => false,
                },
                {
                  label: t("other.last_month"),
                  hasCustomRendering: false,
                  range: () => ({
                    startDate: new Date(
                      date.getFullYear(),
                      date.getMonth() - 1,
                      1
                    ),
                    endDate: new Date(date.getFullYear(), date.getMonth(), 0),
                  }),
                  isSelected() {
                    return false;
                  },
                },
              ]}
              months={2}
              direction="horizontal"
              ranges={selectionRange}
              showSelectionPreview={true}
              moveRangeOnFirstSelection={false}
              minDate={new Date("2022-01-01")}
              maxDate={new Date("2023-12-31")}
              onChange={(item) => setState([item.selection])}
            />
          </Popover>
          <TextField
            label={t(`main.transporttype`)}
            name="sort"
            select
            SelectProps={{ native: true }}
            variant="outlined"
            sx={{ mx: 3 }}
          >
            {transport.map((option) => (
              <option key={option.value} value={option.value}>
                {t(`utils.${option.label.toLocaleLowerCase()}`)}
              </option>
            ))}
          </TextField>
          <TextField
            label={t(`main.departurecity`)}
            name="sort"
            select
            SelectProps={{ native: true }}
            variant="outlined"
            sx={{ mx: 3 }}
          >
            {departureCity.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>
          <TextField
            label={t(`main.period`)}
            name="sort"
            select
            SelectProps={{ native: true }}
            variant="outlined"
            sx={{ mx: 3 }}
          >
            {period.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>
          <TextField
            label={t(`other.board_type`)}
            name="sort"
            select
            SelectProps={{ native: true }}
            variant="outlined"
            sx={{ mx: 3 }}
          >
            {boardType.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </TextField>
        </Box>
        {!data && <BoxedLoader height={"390px"} />}
        {data && <Chart height={390} {...chart} />}
      </CardContent>
    </Card>
  );
};

export default PredictedTrendsForOffer;
